import Choices from "choices.js";
import tippy from "tippy.js";
import { createModal, initializeModalForm } from "./modals";
import { addAlert } from "./alerts";
import { debounce, truncate, escapeHtml } from "./utils";

const toolTipsCache = {};
const choicesObjects = {};

// Per-select abort controllers for in-flight tail fetches.
const tailAbortControllers = new Map();
const HEAD_LIMIT = 100;
const TAIL_LIMIT = 10000;

export function initializeChoices(container) {
  container
    .querySelectorAll(".choicesjs, .choicesjsmultiple")
    .forEach((selectInput) => {
      let noResultsLabel = gettext("No results found");
      if (selectInput.dataset.createUrl) {
        if (selectInput.dataset.createLabel) {
          noResultsLabel = selectInput.dataset.createLabel;
        } else {
          noResultsLabel = gettext("Create new object");
        }
        noResultsLabel = `
            <button type="button" class="btn btn-green create-modal" data-id="${selectInput.id}">
                ${noResultsLabel}
            </button>`;
      }

      const fetchUrl = selectInput.dataset.fetchUrl;
      let searchEnabled = true;
      if (selectInput.dataset.searchenabled === "false") searchEnabled = false;
      const position = selectInput.dataset.position || "auto";
      const searchFloor = selectInput.dataset.searchFloor
        ? Number(selectInput.dataset.searchFloor)
        : 1;
      const searchPlaceholderValue =
        selectInput.dataset.searchPlaceholderValue || null;

      const choicesObject = new Choices(selectInput, {
        noResultsText: noResultsLabel,
        noChoicesText: noResultsLabel,
        shouldSort: false,
        allowHTML: true,
        searchEnabled: searchEnabled,
        searchChoices: fetchUrl ? false : true,
        removeItemButton: selectInput.hasAttribute("multiple"),
        position: position,
        itemSelectText: gettext("Press to select"),
        searchFloor: searchFloor,
        searchPlaceholderValue: searchPlaceholderValue,
      });
      selectInput.choices = choicesObject;
      choicesObjects[selectInput.id] = choicesObject;

      wrapSelectedItem(selectInput);
      addTooltipIcon(selectInput);
      addEditButton(selectInput);

      selectInput.addEventListener("hideDropdown", function (e) {
        choicesObject.clearInput();
      });

      if (fetchUrl) {
        const processSearch = debounce((query) =>
          fetchChoices(choicesObject, query),
        );

        selectInput.addEventListener("showDropdown", function () {
          choicesObject.clearChoices();
          choicesObject.setChoices([
            {
              value: "loading",
              label: gettext("Loading..."),
              disabled: true,
            },
          ]);
          processSearch("");
        });

        selectInput.addEventListener("search", function (e) {
          processSearch(e.detail.value);
        });

        choicesObject.input.element.addEventListener("keyup", (event) => {
          if (
            (event.which === 8 || event.which === 46) &&
            event.target.value === ""
          ) {
            processSearch("");
          }
        });
      }

      if (selectInput.dataset.disabled === "disabled") {
        choicesObject.disable();
      }

      // Store selected label in a hidden field if store-label attribute is set
      if (selectInput.dataset.storeLabel) {
        const storeLabelField = document.querySelector(
          `#id_${selectInput.dataset.storeLabel}`,
        );

        if (storeLabelField) {
          selectInput.addEventListener("choice", function (e) {
            if (e.detail && e.detail.choice && e.detail.choice.label) {
              // Strip HTML tags from label
              const tempDiv = document.createElement("div");
              tempDiv.innerHTML = e.detail.choice.label;
              storeLabelField.value = tempDiv.textContent.trim();
            }
          });
          // Clear label when value is cleared
          selectInput.addEventListener("change", function () {
            if (!selectInput.value) {
              storeLabelField.value = "";
            }
          });
        }
      }

      if (
        selectInput.dataset.parent &&
        !selectInput.hasAttribute("data-is-draft") &&
        !selectInput.hasAttribute("data-is-part-form")
      ) {
        const parentInput = document.querySelector(
          `#id_${selectInput.dataset.parent}`,
        );
        function checkParentInput() {
          if (parentInput.value && !parentInput.disabled) {
            choicesObject.enable();
          } else {
            choicesObject.disable();
          }
        }
        checkParentInput();

        parentInput.addEventListener("change", function (e) {
          checkParentInput();
          choicesObject.removeActiveItems();
        });
      }
    });
}

async function fetchChoices(choicesObject, query) {
  const selectInput = choicesObject.passedElement.element;
  const parent = selectInput.dataset.parent;
  let fetchUrl = selectInput.dataset.fetchUrl;
  let params = selectInput.dataset.fetchParams;
  const parentParams = selectInput.dataset.parentParams;
  if (parent) {
    const parentId = `#id_${parent}`;
    fetchUrl = fetchUrl.replace(
      "/0/",
      `/${document.querySelector(parentId).value}/`,
    );
    if (parentParams) {
      if (params)
        params += `${parentParams}=${document.querySelector(parentId).value}`;
      else params = `${parentParams}=${document.querySelector(parentId).value}`;
    }
  }

  const prevController = tailAbortControllers.get(selectInput.id);
  if (prevController) prevController.abort();

  const trimmedQuery = query.trim();
  const buildUrl = (extra) => {
    const sp = new URLSearchParams({ q: trimmedQuery, ...extra });
    return `${fetchUrl}?${sp}&${params ? params : ""}`;
  };

  const includeIds = selectInput.value || "";
  const headUrl = buildUrl({
    limit: String(HEAD_LIMIT),
    offset: "0",
    include_ids: includeIds,
  });

  let headItems;
  try {
    const response = await fetch(headUrl);
    if (response.status !== 200) {
      choicesObject.clearChoices();
      return;
    }
    headItems = await response.json();
  } catch {
    choicesObject.clearChoices();
    return;
  }

  await renderChoicesIntoDropdown(
    choicesObject,
    selectInput,
    headItems,
    trimmedQuery,
  );

  // Skip tail when searching (head covers it) or dataset already complete.
  if (trimmedQuery || headItems.length < HEAD_LIMIT) return;

  const tailController = new AbortController();
  tailAbortControllers.set(selectInput.id, tailController);
  try {
    const tailUrl = buildUrl({
      offset: String(HEAD_LIMIT),
      limit: String(TAIL_LIMIT),
    });
    const response = await fetch(tailUrl, { signal: tailController.signal });
    if (response.status !== 200) return;
    const tailItems = await response.json();
    if (!tailItems.length) return;

    const seen = new Set(headItems.map((i) => i.value));
    const merged = [...headItems];
    for (const item of tailItems) {
      if (!seen.has(item.value)) merged.push(item);
    }
    await renderChoicesIntoDropdown(
      choicesObject,
      selectInput,
      merged,
      trimmedQuery,
    );
  } catch (err) {
    if (err.name === "AbortError") return;
    console.warn("Background choices fetch failed:", err);
  } finally {
    if (tailAbortControllers.get(selectInput.id) === tailController) {
      tailAbortControllers.delete(selectInput.id);
    }
  }
}

async function renderChoicesIntoDropdown(
  choicesObject,
  selectInput,
  items,
  query,
) {
  let decorated;
  if (selectInput.dataset.detailUrl) {
    decorated = items.map((item) => {
      const detailUrl = selectInput.dataset.detailUrl.replace(
        "/0/",
        `/${item.value}/`,
      );
      return {
        ...item,
        label: `
                ${escapeHtml(item.label)}
                <button type="button" class="choices__detail" data-url="${detailUrl}">
                    ${gettext("i")}
                </button>`,
      };
    });
  } else {
    decorated = items.map((item) => ({
      ...item,
      label: escapeHtml(item.label),
    }));
  }
  items = decorated;
  choicesObject.clearChoices();
  if (items.length > 0 && !query && !selectInput.hasAttribute("required")) {
    items.unshift({
      value: "",
      label: "---------",
    });
  }
  const hasGroups = items.some((item) => item.group);
  if (hasGroups) {
    const groups = new Map();
    const ungrouped = [];
    items.forEach((item) => {
      if (!item.group) {
        ungrouped.push(item);
        return;
      }
      if (!groups.has(item.group)) {
        groups.set(item.group, []);
      }
      groups.get(item.group).push({ value: item.value, label: item.label });
    });
    const grouped = [];
    if (ungrouped.length) grouped.push(...ungrouped);
    let groupId = 1;
    groups.forEach((choices, label) => {
      grouped.push({ label, id: groupId++, choices });
    });
    await choicesObject.setChoices(grouped);
    return;
  }
  await choicesObject.setChoices(items);
}

function addTooltipIcon(element) {
  if (!element.dataset || !element.dataset.detailUrl || !element.value) return;
  const detailUrl = element.dataset.detailUrl.replace(
    "/0/",
    `/${element.value}/`,
  );
  const container = element.parentElement.querySelector(".choices__item");
  container.insertAdjacentHTML(
    "beforeend",
    `<button type="button" class="choices__detail" data-url="${detailUrl}">i</button>`,
  );
}

function wrapSelectedItem(element) {
  const container = element.parentElement.querySelector(".choices__item");
  if (container === null) return;
  // Build via textContent: re-parsing the text as innerHTML would resurrect
  // any markup (e.g. a malicious customer name) that escaping just neutralised.
  const span = document.createElement("span");
  span.className = "choices__selected__text";
  span.textContent = container.childNodes[0]?.textContent ?? "";
  container.replaceChildren(span);
}

function addEditButton(element) {
  if (!element.dataset.updateUrl || !element.value) return;
  const container = element.parentElement.querySelector(".choices__item");
  if (container.querySelector(".choices__edit") === null) {
    container.insertAdjacentHTML(
      "beforeend",
      `<button type="button" class="choices__edit">Edit</button>`,
    );
  }
}

export function addChoice(element, value, label, selected) {
  choicesObjects[element.id].setChoices([
    { value: value, label: label, selected: selected },
  ]);
  if (selected) wrapSelectedItem(element);
  addTooltipIcon(element);
  addEditButton(element);
}

export function setChoices(element, choices) {
  choicesObjects[element.id].setChoices(choices);
  addTooltipIcon(element);
  addEditButton(element);
}

export async function setChoice(element, value, preload) {
  if (preload) {
    await fetchChoices(choicesObjects[element.id], "");
  }
  choicesObjects[element.id].setChoiceByValue(value);
}

export function getChoicesInstance(element) {
  return choicesObjects[element.id];
}

function handleModalSave(e, selectInput, triggerChange) {
  addChoice(
    document.querySelector(`#${selectInput.id}`),
    e.detail.id,
    e.detail.label,
    true,
  );
  if (triggerChange) selectInput.dispatchEvent(new Event("change"));
  if (e.detail.message) {
    addAlert("success", e.detail.message);
  }
}

let mouseDown = 0;
document.body.onmousedown = function () {
  ++mouseDown;
};
document.body.onmouseup = function () {
  if (mouseDown > 0) --mouseDown;
};
document.addEventListener("keydown", function (e) {
  if (e.key === "Control") ++mouseDown;
});
document.addEventListener("keyup", function (e) {
  if (e.key === "Control") --mouseDown;
});

document.addEventListener("mouseover", function (e) {
  const detailsTarget = e.target.closest(".choices__detail");
  if (!mouseDown)
    if (detailsTarget) {
      const url = detailsTarget.dataset.url;
      const tooltip = tippy(detailsTarget, {
        content: gettext("Loading..."),
        allowHTML: true,
        onShow(instance) {
          if (toolTipsCache[url]) {
            instance.setContent(toolTipsCache[url]);
            return;
          }
          fetch(url)
            .then((response) => {
              if (!response.ok) throw new Error(`HTTP ${response.status}`);
              return response.json();
            })
            .then((data) => {
              const values = data && data.values;
              if (!values || Object.keys(values).length === 0) {
                instance.setContent(gettext("No details available"));
                return;
              }
              let valuesString = '<div class="fields">';
              Object.entries(values).forEach((valuePair) => {
                // Escape first, then truncate so truncate's "&hellip;" stays a
                // real entity instead of being double-escaped.
                valuesString += `<div class="field"><i>${escapeHtml(
                  valuePair[0],
                )}</i><b>${truncate(
                  escapeHtml(String(valuePair[1] ?? "")),
                  100,
                )}</b></div>`;
              });
              valuesString += "</div>";
              toolTipsCache[url] = valuesString;
              instance.setContent(valuesString);
            })
            .catch(() => {
              instance.setContent(gettext("Failed to load details"));
            });
        },
        onHidden(instance) {
          instance.destroy();
        },
      });
      if (!mouseDown) {
        tooltip.show();
      } else {
        tooltip.hide();
      }
    }
});

document.addEventListener(
  "click",
  async function (e) {
    const target = e.target;
    const selectInput = target.closest(".choices")
      ? target.closest(".choices").querySelector("select")
      : null;
    if (target.closest(".create-modal")) {
      let createUrl = selectInput.dataset.createUrl;
      const choicesInstance = choicesObjects[selectInput.id];
      let typedQuery = "";

      if (
        choicesInstance &&
        choicesInstance.input &&
        choicesInstance.input.element
      ) {
        typedQuery = choicesInstance.input.element.value.trim();
      }

      const createTargetType = selectInput.dataset.createTargetType;

      if (createTargetType === "customer" && typedQuery) {
        createUrl +=
          (createUrl.includes("?") ? "&" : "?") +
          "q=" +
          encodeURIComponent(typedQuery);
      }
      if (selectInput.dataset.parent) {
        createUrl = createUrl.replace(
          "0",
          document.querySelector(`#id_${selectInput.dataset.parent}`).value,
        );
      }

      const response = await fetch(createUrl, {
        headers: {
          ajax: true,
        },
      });
      const createForm = await response.text();
      const modal = createModal(selectInput.dataset.createLabel, createForm);
      initializeModalForm(modal, undefined, dataTimeFieldFormats);

      modal.addEventListener("modal-form-saved", function (e) {
        handleModalSave(e, selectInput, true);
      });
    } else if (target.closest(".choices__edit")) {
      e.stopPropagation();
      const id = target.closest(".choices__item").dataset.value;
      let updateUrl = selectInput.dataset.updateUrl;
      if (selectInput.dataset.parent) {
        const parentId = `#id_${selectInput.dataset.parent}`;
        updateUrl = updateUrl.replace(
          "/0/",
          `/${document.querySelector(parentId).value}/`,
        );
      }
      updateUrl = updateUrl.replace("/0/", `/${id}/`);
      const response = await fetch(updateUrl, {
        headers: {
          ajax: true,
        },
      });
      const updateForm = await response.text();
      const modal = createModal(selectInput.dataset.updateLabel, updateForm);
      initializeModalForm(modal, undefined, dataTimeFieldFormats);

      modal.addEventListener("modal-form-saved", function (e) {
        handleModalSave(e, selectInput, false);
      });
    }
  },
  true,
);

document.querySelectorAll(".choicesjs").forEach((element) => {
  if (!element.dataset.updateUrl) return;
  element.addEventListener("change", function (e) {
    wrapSelectedItem(this);
    addTooltipIcon(this);
    addEditButton(this);
  });
});

window.addEventListener("DOMContentLoaded", async function () {
  initializeChoices(document);
});
export default {
  Choices,
  initializeChoices,
  addChoice,
  setChoice,
  setChoices,
  getChoicesInstance,
};
